import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Clock, ChefHat, Heart, X, Info, Leaf, CalendarDays, Plus, Globe, Users, Copy, Share2, Check, LayoutGrid, Settings, ChevronRight, Sparkles, Star, RefreshCw } from 'lucide-react';
import { DIET_MEALS, MEAL_CATEGORIES, MEAL_COUNTRIES, DIET_DISCLAIMER } from '../data/dietMeals';
import { localizedTitle } from '../data/mealTitleTranslations';
import { localizedRecoveryNote, localizedIngredients, localizedSteps } from '../data/mealContentTranslations';
import { localizedTag } from '../data/mealTagTranslations';
import { imageForMeal } from '../data/mealImages';
import { useLanguage } from '../contexts/LanguageContext';
import { authFetch } from '../lib/authFetch';
import { toast } from 'sonner';
import MealPlanner from './MealPlanner';
import MyRecipesView from './MyRecipesView';

function MealCard({ meal, onOpen, lang }) {
  const isVeg = (meal.tags || []).includes('vegetarian') || (meal.tags || []).includes('vegan');
  const title = localizedTitle(meal, lang);
  return (
    <button
      onClick={() => onOpen(meal)}
      className="text-left bg-white border border-zinc-200 rounded-xl overflow-hidden hover:border-blue-400 transition-all active:scale-[0.99]"
      data-testid={`meal-card-${meal.id}`}
    >
      <div className="aspect-[4/3] bg-zinc-100 overflow-hidden">
        <img
          src={imageForMeal(meal)}
          alt={title}
          loading="lazy"
          className="w-full h-full object-cover"
          onError={(e) => { e.currentTarget.src = meal.image; e.currentTarget.style.opacity = '0.4'; }}
        />
      </div>
      <div className="p-3">
        <div className="flex items-start justify-between gap-2 mb-1.5">
          <h4 className="text-sm font-bold text-zinc-900 leading-tight flex-1">{title}</h4>
          {isVeg && (
            <span className="text-[10px] font-bold bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full flex items-center gap-0.5 flex-shrink-0">
              <Leaf className="w-2.5 h-2.5" />
              VEG
            </span>
          )}
        </div>
        <div className="flex items-center gap-3 text-[11px] text-zinc-500">
          <span className="flex items-center gap-1">
            <Clock className="w-3 h-3" />
            {meal.prep_minutes + meal.cook_minutes} min
          </span>
          <span className="flex items-center gap-1 capitalize">
            <ChefHat className="w-3 h-3" />
            {meal.difficulty}
          </span>
        </div>
      </div>
    </button>
  );
}

// Pinterest-style masonry tile.  CSS columns provide the masonry — no JS lib.
// `break-inside-avoid` keeps each tile whole.  Heights vary slightly by ID
// for visual rhythm.
function GalleryTile({ meal, onOpen, lang }) {
  const flag = (MEAL_COUNTRIES.find(c => c.id === meal.country) || {}).flag || '';
  const title = localizedTitle(meal, lang);
  // Three rotating aspect ratios for masonry rhythm — deterministic by id hash.
  const ratios = ['aspect-[3/4]', 'aspect-[4/5]', 'aspect-[1/1]'];
  const idx = (meal.id.charCodeAt(0) + meal.id.length) % ratios.length;
  return (
    <button
      onClick={() => onOpen(meal)}
      className={`group relative w-full mb-3 break-inside-avoid overflow-hidden rounded-xl bg-zinc-100 transition-transform hover:-translate-y-0.5`}
      data-testid={`gallery-tile-${meal.id}`}
    >
      <div className={`${ratios[idx]} w-full overflow-hidden`}>
        <img
          src={imageForMeal(meal)}
          alt={title}
          loading="lazy"
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          onError={(e) => { e.currentTarget.src = meal.image; e.currentTarget.style.opacity = '0.4'; }}
        />
      </div>
      {/* Bottom gradient overlay with title + flag */}
      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/85 via-black/40 to-transparent p-2.5 pt-8 text-left">
        <div className="flex items-end justify-between gap-2">
          <h4 className="text-[12px] font-semibold text-white leading-tight line-clamp-2 flex-1">{title}</h4>
          {flag && <span className="text-base leading-none flex-shrink-0">{flag}</span>}
        </div>
      </div>
    </button>
  );
}

function CookTogetherModal({ meal, onClose }) {
  const defaultMessage = `Hey: I'm in Recovery and one of the things I'm building back is the simple ritual of cooking with the people who matter to me. Would you be up for making this with me one evening?\n\n${meal.title}\nPrep ${meal.prep_minutes} min · cook ${meal.cook_minutes} min · ${meal.difficulty}\n\nNo pressure, no occasion needed: just a sober dinner and good company. Let me know what night works for you.`;
  const [message, setMessage] = useState(defaultMessage);
  const [recipient, setRecipient] = useState('sponsor');
  const apiBase = process.env.REACT_APP_BACKEND_URL;

  const trackInvite = async () => {
    try {
      await authFetch(`${apiBase}/api/user/cook-together-invite`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ meal_id: meal.id, recipient }),
      });
    } catch (e) { /* ignore: tracking is best-effort */ }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(message);
      toast.success('Invitation copied: paste it into your texts, email or WhatsApp.');
      trackInvite();
    } catch (e) {
      toast.error('Could not access clipboard. Select and copy manually.');
    }
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: `Cook ${meal.title} together`, text: message });
        trackInvite();
      } catch (e) { /* user cancelled */ }
    } else {
      handleCopy();
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-[60] flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose}>
      <div className="bg-white w-full max-w-lg max-h-[92vh] overflow-y-auto rounded-t-2xl sm:rounded-2xl" onClick={e => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-zinc-200 flex items-center justify-between">
          <h3 className="text-base font-bold flex items-center gap-2"><Users className="w-4 h-4 text-blue-600" /> Cook with a Recovery friend</h3>
          <button onClick={onClose} className="p-1 text-zinc-500" data-testid="cook-together-close"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-5 space-y-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-xs text-blue-900 leading-relaxed">
              <strong>The Recovery social ritual.</strong> Active addiction isolates. One of the strongest evidence-based protectors of long-term sobriety is rebuilding small, repeated, sober social rituals: and shared cooking is one of the easiest. Invite your sponsor, a Recovery peer or a family member to make this with you. The chopping, stirring and waiting all become moments of unhurried connection that you used to spend at the bar.
            </p>
          </div>

          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2 block">Who are you inviting?</label>
            <div className="flex flex-wrap gap-2">
              {[
                { id: 'sponsor', label: 'My sponsor' },
                { id: 'peer', label: 'A Recovery peer' },
                { id: 'family', label: 'Family member' },
                { id: 'friend', label: 'Sober friend' },
              ].map(r => (
                <button
                  key={r.id}
                  onClick={() => setRecipient(r.id)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border ${recipient === r.id ? 'bg-zinc-900 text-white border-zinc-900' : 'bg-white border-zinc-200 text-zinc-700'}`}
                  data-testid={`cook-recipient-${r.id}`}
                >
                  {r.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2 block">Your invitation</label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={9}
              className="w-full p-3 rounded-lg border border-zinc-200 text-sm leading-relaxed"
              data-testid="cook-message-textarea"
            />
            <p className="text-[11px] text-zinc-400 mt-1">Edit anything you want: it's yours.</p>
          </div>

          <div className="flex flex-col sm:flex-row gap-2">
            <button
              onClick={handleShare}
              className="flex-1 flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl py-3 text-sm font-semibold"
              data-testid="cook-share-btn"
            >
              <Share2 className="w-4 h-4" />
              Share invitation
            </button>
            <button
              onClick={handleCopy}
              className="flex-1 flex items-center justify-center gap-2 bg-zinc-100 hover:bg-zinc-200 text-zinc-800 rounded-xl py-3 text-sm font-semibold"
              data-testid="cook-copy-btn"
            >
              <Copy className="w-4 h-4" />
              Copy text
            </button>
          </div>

          <p className="text-[11px] text-zinc-400 text-center">A sober shared meal is a small thing: and small things repeated are how Recovery is built.</p>
        </div>
      </div>
    </div>
  );
}

function MealDetailModal({ meal, onClose, prefs }) {
  const { language } = useLanguage();
  const [adding, setAdding] = useState(false);
  const [showCookTogether, setShowCookTogether] = useState(false);
  const [marking, setMarking] = useState(false);
  const [marked, setMarked] = useState(false);
  const [swapData, setSwapData] = useState({}); // { ingredientText: { loading, suggestions } }
  const apiBase = process.env.REACT_APP_BACKEND_URL;
  const isAiMeal = (meal?.id || '').startsWith('ai-');
  // For AI meals, the raw English fields are the source of truth (no localisation map yet).
  const title = meal ? (isAiMeal ? meal.title : localizedTitle(meal, language)) : '';
  const ingredients = meal ? (isAiMeal ? (meal.ingredients || []) : localizedIngredients(meal, language)) : [];
  const steps = meal ? (isAiMeal ? (meal.steps || []) : localizedSteps(meal, language)) : [];
  const recoveryNote = meal ? (isAiMeal ? (meal.recovery_note || '') : localizedRecoveryNote(meal, language)) : '';

  // C3: which ingredients contain a disliked term — those get a "Swap" pill
  const dislikes = (prefs?.dislikes || []).filter(d => d && String(d).trim()).map(d => String(d).toLowerCase().trim());
  const matchedDislikeFor = (ingredientText) => {
    const lower = String(ingredientText || '').toLowerCase();
    return dislikes.find(d => lower.includes(d)) || null;
  };

  const fetchSwap = async (ingredient) => {
    const key = ingredient.toLowerCase().trim();
    setSwapData(prev => ({ ...prev, [key]: { ...(prev[key] || {}), loading: true } }));
    try {
      const r = await authFetch(`${apiBase}/api/diet/ingredient-swap`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ingredient: key }),
      });
      if (r.ok) {
        const d = await r.json();
        setSwapData(prev => ({ ...prev, [key]: { loading: false, suggestions: d.suggestions || [] } }));
      } else {
        setSwapData(prev => ({ ...prev, [key]: { loading: false, suggestions: [] } }));
        toast.error('Could not fetch swap suggestions');
      }
    } catch (_) {
      setSwapData(prev => ({ ...prev, [key]: { loading: false, suggestions: [] } }));
      toast.error('Network error');
    }
  };

  const handleMarkCooked = async () => {
    setMarking(true);
    try {
      const res = await authFetch(`${apiBase}/api/user/meal-cooked`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ meal_id: meal.id }),
      });
      if (res.ok) {
        setMarked(true);
        toast.success('Logged. One more sober meal in the books.');
      } else {
        toast.error('Could not log this meal.');
      }
    } catch (e) {
      toast.error('Network error.');
    }
    setMarking(false);
  };

  const handleAddToPlan = async () => {
    setAdding(true);
    try {
      // Fetch current plan
      const res = await authFetch(`${apiBase}/api/user/meal-plan`);
      const data = res.ok ? await res.json() : { plan: {} };
      const plan = data.plan || {};

      // Find first empty slot for this category in any day, or default to monday
      const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
      let chosenDay = null;
      for (const d of days) {
        const dayPlan = plan[d] || {};
        if (!dayPlan[meal.category]) { chosenDay = d; break; }
      }
      if (!chosenDay) chosenDay = 'monday'; // overwrite Monday if all full

      const next = { ...plan };
      next[chosenDay] = { ...(next[chosenDay] || {}), [meal.category]: meal.id };

      const saveRes = await authFetch(`${apiBase}/api/user/meal-plan`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ plan: next }),
      });
      if (saveRes.ok) {
        toast.success(`Added to ${chosenDay.charAt(0).toUpperCase() + chosenDay.slice(1)}'s ${meal.category}`);
      } else {
        toast.error('Could not save to plan');
      }
    } catch (e) {
      toast.error('Network error: please try again');
    }
    setAdding(false);
  };

  if (!meal) return null;
  return (
    <div
      className="fixed inset-0 bg-black/60 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={onClose}
      data-testid="meal-detail-modal"
    >
      <div
        className="bg-white w-full max-w-2xl max-h-[92vh] sm:max-h-[88vh] overflow-y-auto rounded-t-2xl sm:rounded-2xl"
        onClick={e => e.stopPropagation()}
      >
        <div className="relative aspect-[16/9] bg-zinc-100">
          <img
            src={imageForMeal(meal)}
            alt={title}
            className="w-full h-full object-cover"
            onError={(e) => { e.currentTarget.src = meal.image; e.currentTarget.style.opacity = '0.4'; }}
          />
          <button
            onClick={onClose}
            className="absolute top-3 right-3 w-9 h-9 rounded-full bg-white/90 hover:bg-white flex items-center justify-center shadow-md"
            data-testid="meal-detail-close"
          >
            <X className="w-5 h-5 text-zinc-700" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div>
            <h2 className="text-xl font-bold text-zinc-900">{title}</h2>
            {title !== meal.title && (
              <p className="text-xs text-zinc-400 mt-0.5">{meal.title}</p>
            )}
            <div className="flex flex-wrap items-center gap-2 mt-2 text-xs text-zinc-600">
              <span className="flex items-center gap-1 bg-zinc-100 px-2 py-1 rounded-full">
                <Clock className="w-3 h-3" />
                Prep {meal.prep_minutes} • Cook {meal.cook_minutes} min
              </span>
              <span className="flex items-center gap-1 bg-zinc-100 px-2 py-1 rounded-full capitalize">
                <ChefHat className="w-3 h-3" />
                {meal.difficulty}
              </span>
              {(meal.tags || []).map(tag => (
                <span key={tag} className="text-[10px] uppercase tracking-wider bg-blue-50 text-blue-700 px-2 py-1 rounded-full font-semibold">
                  {localizedTag(tag, language)}
                </span>
              ))}
            </div>
          </div>

          {/* Save to plan CTA */}
          <button
            onClick={handleAddToPlan}
            disabled={adding}
            className="w-full flex items-center justify-center gap-2 bg-blue-600 text-white rounded-xl py-3 text-sm font-semibold hover:bg-blue-700 disabled:opacity-50 transition-colors"
            data-testid="save-to-plan-btn"
          >
            <Plus className="w-4 h-4" />
            {adding ? 'Saving…' : 'Save to my plan'}
          </button>

          {/* Cook with a Recovery friend CTA */}
          <button
            onClick={() => setShowCookTogether(true)}
            className="w-full flex items-center justify-center gap-2 bg-white border-2 border-zinc-200 hover:border-blue-300 hover:bg-blue-50 text-zinc-800 rounded-xl py-3 text-sm font-semibold transition-colors"
            data-testid="cook-together-btn"
          >
            <Users className="w-4 h-4 text-blue-600" />
            Cook with a Recovery friend
          </button>

          <div>
            <h3 className="text-sm font-bold text-zinc-900 mb-2">Ingredients</h3>
            <ul className="space-y-1.5">
              {ingredients.map((ing, i) => {
                const dislikedTerm = matchedDislikeFor(ing);
                const swapKey = (dislikedTerm || '').toLowerCase();
                const swap = swapKey ? swapData[swapKey] : null;
                return (
                  <li key={i} className="text-sm text-zinc-700">
                    <div className="flex gap-2 items-start">
                      <span className="text-blue-500 flex-shrink-0 mt-0.5">•</span>
                      <span className="flex-1">{ing}</span>
                      {dislikedTerm && (
                        <button
                          onClick={() => fetchSwap(dislikedTerm)}
                          disabled={swap?.loading}
                          className="flex-shrink-0 inline-flex items-center gap-0.5 text-[10px] font-bold uppercase tracking-wider bg-amber-100 text-amber-700 hover:bg-amber-200 px-1.5 py-0.5 rounded-md disabled:opacity-60"
                          data-testid={`swap-${dislikedTerm}`}
                        >
                          <RefreshCw className={`w-2.5 h-2.5 ${swap?.loading ? 'animate-spin' : ''}`} />
                          Swap &ldquo;{dislikedTerm}&rdquo;
                        </button>
                      )}
                    </div>
                    {swap && swap.suggestions && swap.suggestions.length > 0 && (
                      <div className="ml-5 mt-1.5 bg-amber-50 border border-amber-200 rounded-lg p-2 space-y-1" data-testid={`swap-suggestions-${dislikedTerm}`}>
                        {swap.suggestions.map((s, si) => (
                          <div key={si} className="text-[11px]">
                            <span className="font-semibold text-amber-900">→ {s.swap}</span>
                            <span className="text-amber-800"> · {s.why}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>

          <div>
            <h3 className="text-sm font-bold text-zinc-900 mb-2">Method</h3>
            <ol className="space-y-2">
              {steps.map((step, i) => (
                <li key={i} className="text-sm text-zinc-700 flex gap-3">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-600 text-white text-xs font-bold flex items-center justify-center">
                    {i + 1}
                  </span>
                  <span className="flex-1 pt-0.5">{step}</span>
                </li>
              ))}
            </ol>
          </div>

          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 flex gap-2">
            <Heart className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-xs font-bold text-emerald-800 uppercase tracking-wider mb-1">
                Why this supports Recovery
              </p>
              <p className="text-sm text-emerald-900 leading-relaxed">{recoveryNote}</p>
            </div>
          </div>
        </div>
      </div>
      {showCookTogether && <CookTogetherModal meal={meal} onClose={() => setShowCookTogether(false)} />}
    </div>
  );
}

export default function DietTab() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const [view, setView] = useState('browse'); // 'browse' | 'plan'
  const [activeCategory, setActiveCategory] = useState('breakfast');
  const [activeCountry, setActiveCountry] = useState('all');
  const [vegOnly, setVegOnly] = useState(false);
  const [selectedMeal, setSelectedMeal] = useState(null);

  // Auto-open a meal if ?meal=<id> is in the URL (e.g. from "Tonight's idea" widget)
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    const mealId = params.get('meal');
    if (!mealId) return;
    const meal = DIET_MEALS.find(m => m.id === mealId);
    if (!meal) return;
    setActiveCategory(meal.category);
    if (meal.country && meal.country !== 'western') setActiveCountry(meal.country);
    setSelectedMeal(meal);
  }, []);
  const [prefs, setPrefs] = useState({ allergies: [], cuisines: [], dislikes: [], vegetarian: false, vegan: false, gluten_free: false, lactose_free: false, halal: false, kosher: false, no_pork: false, diabetic: false });
  const [respectPrefs, setRespectPrefs] = useState(true);

  // Load user preferences (from /api/user/diet-preferences)
  useEffect(() => {
    (async () => {
      try {
        const apiBase = process.env.REACT_APP_BACKEND_URL;
        const res = await authFetch(`${apiBase}/api/user/diet-preferences`);
        if (res.ok) {
          const data = await res.json();
          if (data && data.preferences) {
            setPrefs({ ...prefs, ...data.preferences });
            if (data.preferences.vegetarian || data.preferences.vegan) setVegOnly(true);
          }
        }
      } catch (e) { /* ignore */ }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- one-shot fetch on mount

  // Filter meals based on prefs (only when respectPrefs is true)
  const matchesPrefs = (meal) => {
    if (!respectPrefs) return true;
    const tags = meal.tags || [];
    const ingText = (meal.ingredients || []).join(' ').toLowerCase();
    if (prefs.vegan && !tags.includes('vegan')) return false;
    if (prefs.vegetarian && !(tags.includes('vegetarian') || tags.includes('vegan'))) return false;
    if (prefs.gluten_free && !tags.includes('gluten-free')) return false;
    if (prefs.lactose_free && /(milk|cheese|yoghurt|yogurt|cream|butter|mozzarella|ricotta|parmesan|feta|halloumi|gruyère|gruyere)/.test(ingText)) return false;
    if (prefs.halal && /(pork|bacon|ham|chorizo|prosciutto|pancetta|salami|sausage)/.test(ingText)) return false;
    if (prefs.no_pork && /(pork|bacon|ham|chorizo|prosciutto|pancetta|salami)/.test(ingText)) return false;
    if (prefs.kosher && /(pork|bacon|ham|chorizo|prosciutto|pancetta|salami|prawns|shellfish|shrimp|sardines)/.test(ingText)) return false;
    // Preferred cuisines (multi-select).  If empty, no cuisine restriction.
    if ((prefs.cuisines || []).length > 0 && meal.country && !prefs.cuisines.includes(meal.country)) return false;
    // Dislikes (substring match in ingredients)
    for (const dislike of prefs.dislikes || []) {
      const d = (dislike || '').toLowerCase().trim();
      if (!d) continue;
      if (ingText.includes(d)) return false;
    }
    // Allergies (array of strings)
    for (const allergy of prefs.allergies || []) {
      const a = (allergy || '').toLowerCase().trim();
      if (!a) continue;
      if (ingText.includes(a)) return false;
    }
    return true;
  };

  const filteredMeals = useMemo(() => {
    return DIET_MEALS.filter(m => {
      if (m.category !== activeCategory) return false;
      if (activeCountry !== 'all' && m.country !== activeCountry) return false;
      if (vegOnly) {
        const tags = m.tags || [];
        if (!(tags.includes('vegetarian') || tags.includes('vegan'))) return false;
      }
      return matchesPrefs(m);
    });
  }, [activeCategory, activeCountry, vegOnly, prefs, respectPrefs]); // eslint-disable-line react-hooks/exhaustive-deps -- matchesPrefs is a closure over prefs which is already in deps

  const totalVeg = DIET_MEALS.filter(m => (m.tags || []).includes('vegetarian') || (m.tags || []).includes('vegan')).length;

  // ============ C1: Relax-filter hints (deterministic, no AI) ============
  // For each currently-applied dietary filter, compute how many additional
  // meals would appear in the current category if it were removed.
  const relaxationHints = useMemo(() => {
    if (!respectPrefs) return [];
    const baseInCategory = DIET_MEALS.filter(m => m.category === activeCategory);
    const passWithout = (omit) => {
      const tweaked = { ...prefs };
      if (omit.kind === 'flag') tweaked[omit.key] = false;
      else if (omit.kind === 'cuisine') tweaked.cuisines = (tweaked.cuisines || []).filter(c => c !== omit.value);
      else if (omit.kind === 'dislike') tweaked.dislikes = (tweaked.dislikes || []).filter(d => d !== omit.value);
      else if (omit.kind === 'allergy') tweaked.allergies = (tweaked.allergies || []).filter(a => a !== omit.value);
      return baseInCategory.filter(m => {
        const tags = m.tags || [];
        const ingText = (m.ingredients || []).join(' ').toLowerCase();
        if (tweaked.vegan && !tags.includes('vegan')) return false;
        if (tweaked.vegetarian && !(tags.includes('vegetarian') || tags.includes('vegan'))) return false;
        if (tweaked.gluten_free && !tags.includes('gluten-free')) return false;
        if (tweaked.lactose_free && /(milk|cheese|yoghurt|yogurt|cream|butter|mozzarella|ricotta|parmesan|feta|halloumi|gruyère|gruyere)/.test(ingText)) return false;
        if (tweaked.halal && /(pork|bacon|ham|chorizo|prosciutto|pancetta|salami|sausage)/.test(ingText)) return false;
        if (tweaked.no_pork && /(pork|bacon|ham|chorizo|prosciutto|pancetta|salami)/.test(ingText)) return false;
        if (tweaked.kosher && /(pork|bacon|ham|chorizo|prosciutto|pancetta|salami|prawns|shellfish|shrimp|sardines)/.test(ingText)) return false;
        if ((tweaked.cuisines || []).length > 0 && m.country && !tweaked.cuisines.includes(m.country)) return false;
        for (const d of tweaked.dislikes || []) { const x = (d || '').toLowerCase().trim(); if (x && ingText.includes(x)) return false; }
        for (const a of tweaked.allergies || []) { const x = (a || '').toLowerCase().trim(); if (x && ingText.includes(x)) return false; }
        return true;
      }).length;
    };
    const candidates = [];
    if (prefs.vegetarian) candidates.push({ kind: 'flag', key: 'vegetarian', label: 'Vegetarian-only' });
    if (prefs.vegan) candidates.push({ kind: 'flag', key: 'vegan', label: 'Vegan-only' });
    if (prefs.gluten_free) candidates.push({ kind: 'flag', key: 'gluten_free', label: 'Gluten-free' });
    if (prefs.lactose_free) candidates.push({ kind: 'flag', key: 'lactose_free', label: 'Lactose-free' });
    if (prefs.halal) candidates.push({ kind: 'flag', key: 'halal', label: 'Halal' });
    if (prefs.kosher) candidates.push({ kind: 'flag', key: 'kosher', label: 'Kosher' });
    for (const c of prefs.cuisines || []) candidates.push({ kind: 'cuisine', value: c, label: `Cuisine: ${c}` });
    for (const d of prefs.dislikes || []) candidates.push({ kind: 'dislike', value: d, label: `Avoiding: ${d}` });
    for (const a of prefs.allergies || []) candidates.push({ kind: 'allergy', value: a, label: `Allergy: ${a}` });
    const baseCount = filteredMeals.length;
    return candidates
      .map(c => ({ ...c, unlocks: passWithout(c) - baseCount }))
      .filter(c => c.unlocks > 0)
      .sort((a, b) => b.unlocks - a.unlocks)
      .slice(0, 3);
  }, [prefs, respectPrefs, activeCategory, filteredMeals.length]);

  // ============ C2: AI Chef state ============
  const [aiMeals, setAiMeals] = useState([]);
  const [aiLoading, setAiLoading] = useState(false);
  const [savedRecipeIds, setSavedRecipeIds] = useState(new Set());

  // Load saved-recipe ids on mount so the favourite-star reflects state
  useEffect(() => {
    (async () => {
      try {
        const r = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/user/my-recipes`);
        if (r.ok) {
          const d = await r.json();
          setSavedRecipeIds(new Set((d.recipes || []).map(rec => rec.meal?.id).filter(Boolean)));
        }
      } catch (_) { /* swallow */ }
    })();
  }, []);

  const generateAiMeals = async () => {
    setAiLoading(true);
    try {
      const r = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/diet/ai-chef`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ preferences: prefs }),
      });
      if (r.ok) {
        const d = await r.json();
        setAiMeals(d.meals || []);
        toast.success(d.cached ? 'AI Chef recipes ready (from cache)' : `AI Chef generated ${d.meals?.length || 0} fresh recipes`);
      } else {
        toast.error('AI Chef is unavailable right now');
      }
    } catch (_) {
      toast.error('Network error: please try again');
    }
    setAiLoading(false);
  };

  const toggleSaveRecipe = async (meal) => {
    const isSaved = savedRecipeIds.has(meal.id);
    try {
      if (isSaved) {
        const r = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/user/my-recipes/${encodeURIComponent(meal.id)}`, { method: 'DELETE' });
        if (r.ok) {
          setSavedRecipeIds(prev => { const n = new Set(prev); n.delete(meal.id); return n; });
          toast.message('Removed from My Recipes');
        }
      } else {
        const r = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/user/my-recipes`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ meal }),
        });
        if (r.ok) {
          setSavedRecipeIds(prev => new Set(prev).add(meal.id));
          toast.success('Saved to My Recipes');
        }
      }
    } catch (_) {
      toast.error('Network error');
    }
  };

  // Count active dietary filters — drives the "preferences applied" banner copy
  const totalMealsInCategory = DIET_MEALS.filter(m => m.category === activeCategory).length;
  const hasActivePrefs = (
    prefs.vegetarian || prefs.vegan || prefs.gluten_free || prefs.lactose_free ||
    prefs.halal || prefs.kosher || prefs.no_pork || prefs.diabetic ||
    (prefs.cuisines || []).length > 0 ||
    (prefs.dislikes || []).length > 0 ||
    (prefs.allergies || []).length > 0
  );

  return (
    <div className="space-y-4" data-testid="diet-tab">
      {/* Preferences shortcut: always visible.  Click "Edit" to jump to the
          full preferences form on /diet-recovery. */}
      <div
        className="bg-emerald-50 border border-emerald-200 rounded-xl px-3 py-2.5 flex items-center gap-3"
        data-testid="diet-prefs-banner"
      >
        <Settings className="w-4 h-4 text-emerald-700 flex-shrink-0" />
        <div className="flex-1 min-w-0 text-[11px] leading-snug">
          {hasActivePrefs ? (
            <span className="text-emerald-900">
              <span className="font-bold">Filtered by your preferences.</span>
              {' '}Showing <span className="font-bold tabular-nums">{filteredMeals.length}</span> of {totalMealsInCategory} meals in this category.
            </span>
          ) : (
            <span className="text-emerald-900">
              <span className="font-bold">No dietary preferences set.</span> Add allergies, preferred cuisines and dislikes to personalise the library.
            </span>
          )}
        </div>
        <button
          onClick={() => navigate('/diet-recovery')}
          className="flex-shrink-0 inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider text-emerald-700 hover:text-emerald-900 hover:bg-emerald-100 px-2 py-1 rounded-md transition-colors"
          data-testid="diet-prefs-edit-btn"
        >
          {hasActivePrefs ? 'Edit' : 'Set up'}
          <ChevronRight className="w-3 h-3" />
        </button>
      </div>

      {/* Top view toggle: Browse / Gallery / My Plan */}
      <div className="flex gap-1 bg-white border border-zinc-200 rounded-xl p-1">
        <button
          onClick={() => setView('browse')}
          className={`flex-1 py-2 rounded-lg text-xs font-semibold transition-colors flex items-center justify-center gap-1.5 ${
            view === 'browse' ? 'bg-emerald-600 text-white' : 'text-zinc-600 hover:bg-zinc-50'
          }`}
          data-testid="diet-view-browse"
        >
          <Leaf className="w-3.5 h-3.5" />
          Browse
        </button>
        <button
          onClick={() => setView('gallery')}
          className={`flex-1 py-2 rounded-lg text-xs font-semibold transition-colors flex items-center justify-center gap-1.5 ${
            view === 'gallery' ? 'bg-emerald-600 text-white' : 'text-zinc-600 hover:bg-zinc-50'
          }`}
          data-testid="diet-view-gallery"
        >
          <LayoutGrid className="w-3.5 h-3.5" />
          Gallery
        </button>
        <button
          onClick={() => setView('plan')}
          className={`flex-1 py-2 rounded-lg text-xs font-semibold transition-colors flex items-center justify-center gap-1.5 ${
            view === 'plan' ? 'bg-emerald-600 text-white' : 'text-zinc-600 hover:bg-zinc-50'
          }`}
          data-testid="diet-view-plan"
        >
          <CalendarDays className="w-3.5 h-3.5" />
          My plan
        </button>
        <button
          onClick={() => setView('recipes')}
          className={`flex-1 py-2 rounded-lg text-xs font-semibold transition-colors flex items-center justify-center gap-1.5 ${
            view === 'recipes' ? 'bg-violet-600 text-white' : 'text-zinc-600 hover:bg-zinc-50'
          }`}
          data-testid="diet-view-recipes"
        >
          <Star className="w-3.5 h-3.5" />
          My recipes
        </button>
      </div>

      {view === 'plan' && <MealPlanner />}

      {view === 'recipes' && (
        <MyRecipesView
          onOpenMeal={(m) => setSelectedMeal(m)}
          onRemove={(mealId) => setSavedRecipeIds(prev => { const n = new Set(prev); n.delete(mealId); return n; })}
        />
      )}

      {view === 'gallery' && (
        <>
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 flex items-center gap-2.5">
            <LayoutGrid className="w-4 h-4 text-emerald-700 flex-shrink-0" />
            <p className="text-[11px] text-emerald-900 leading-relaxed">
              Photo gallery: every meal in the library, in one scroll.  Tap any photo for the full recipe.
              {' '}Filtered by your category / cuisine / vegetarian choices below.
            </p>
          </div>

          {/* Same filter row used in Browse, scaled to gallery */}
          <div className="flex gap-1.5 overflow-x-auto pb-1">
            {MEAL_CATEGORIES.map(cat => {
              const count = DIET_MEALS.filter(m => m.category === cat.id).length;
              return (
                <button
                  key={cat.id}
                  onClick={() => setActiveCategory(cat.id)}
                  className={`flex-shrink-0 px-3 py-1.5 rounded-full text-[11px] font-semibold transition-colors flex items-center gap-1 ${
                    activeCategory === cat.id ? 'bg-emerald-600 text-white' : 'bg-white border border-zinc-200 text-zinc-700 hover:border-emerald-300'
                  }`}
                  data-testid={`gallery-cat-${cat.id}`}
                >
                  <span>{cat.icon}</span>{cat.label} <span className="opacity-60">({count})</span>
                </button>
              );
            })}
          </div>
          <div className="flex gap-1.5 overflow-x-auto pb-1">
            {MEAL_COUNTRIES.map(c => (
              <button
                key={c.id}
                onClick={() => setActiveCountry(c.id)}
                className={`flex-shrink-0 px-3 py-1.5 rounded-full text-[11px] font-semibold transition-colors flex items-center gap-1 ${
                  activeCountry === c.id ? 'bg-blue-600 text-white' : 'bg-white border border-zinc-200 text-zinc-700 hover:border-blue-300'
                }`}
                data-testid={`gallery-country-${c.id}`}
              >
                <span>{c.flag}</span>{c.label}
              </button>
            ))}
          </div>

          {filteredMeals.length === 0 ? (
            <div className="bg-white border border-zinc-200 rounded-xl p-6 text-center">
              <p className="text-sm text-zinc-500">No meals match this combination: try widening your filters.</p>
            </div>
          ) : (
            <div
              className="columns-2 sm:columns-3 lg:columns-4 gap-3"
              data-testid="diet-gallery-grid"
            >
              {filteredMeals.map(meal => (
                <GalleryTile key={meal.id} meal={meal} onOpen={setSelectedMeal} lang={language} />
              ))}
            </div>
          )}

          <p className="text-[10px] text-zinc-400 text-center pt-1">
            {filteredMeals.length} meal{filteredMeals.length === 1 ? '' : 's'} · bespoke food photography by Axiom
          </p>
        </>
      )}

      {view === 'browse' && (
        <>
          {/* Intro */}
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
            <h3 className="text-sm font-bold text-emerald-900 mb-1 flex items-center gap-2">
              <Leaf className="w-4 h-4" />
              Diet for Recovery: a Daily Recovery Tool
            </h3>
            <p className="text-xs text-emerald-800 leading-relaxed">
              Simple, healthy, achievable meals that support long-term Recovery. Good food stabilises blood-sugar, restores depleted nutrients and reduces the cravings that fuel relapse. Tap any meal to see the recipe and "Save to my plan" to build your weekly schedule. Currently {DIET_MEALS.length} meals ({totalVeg} vegetarian/vegan): more added regularly.
            </p>
          </div>

          {/* Category tabs */}
          <div className="flex gap-1 overflow-x-auto pb-1 -mx-1 px-1">
            {MEAL_CATEGORIES.map(cat => {
              const isActive = activeCategory === cat.id;
              return (
                <button
                  key={cat.id}
                  onClick={() => setActiveCategory(cat.id)}
                  className={`flex-shrink-0 flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold transition-colors ${
                    isActive ? 'bg-zinc-900 text-white' : 'bg-white border border-zinc-200 text-zinc-700 hover:bg-zinc-50'
                  }`}
                  data-testid={`diet-category-${cat.id}`}
                >
                  <span>{cat.icon}</span>
                  {cat.label}
                </button>
              );
            })}
          </div>

          {/* Country selector */}
          <div className="flex gap-1.5 overflow-x-auto pb-1 -mx-1 px-1" data-testid="diet-country-row">
            {MEAL_COUNTRIES.map(c => {
              const isActive = activeCountry === c.id;
              return (
                <button
                  key={c.id}
                  onClick={() => setActiveCountry(c.id)}
                  className={`flex-shrink-0 flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-[11px] font-medium border transition-colors ${
                    isActive ? 'bg-zinc-900 text-white border-zinc-900' : 'bg-white border-zinc-200 text-zinc-600 hover:bg-zinc-50'
                  }`}
                  data-testid={`diet-country-${c.id}`}
                >
                  <span className="text-sm">{c.flag}</span>
                  {c.label}
                </button>
              );
            })}
          </div>

          {/* Preferences toggle */}
          {(prefs.allergies?.length > 0 || prefs.vegetarian || prefs.vegan || prefs.gluten_free || prefs.lactose_free || prefs.halal || prefs.kosher || prefs.no_pork || prefs.diabetic) && (
            <div className="flex items-center justify-between bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
              <span className="text-xs font-medium text-blue-900 flex items-center gap-1.5">
                <Globe className="w-3.5 h-3.5" />
                Filter by my dietary preferences
              </span>
              <button
                onClick={() => setRespectPrefs(!respectPrefs)}
                role="switch"
                aria-checked={respectPrefs}
                className={`relative w-10 h-5 rounded-full transition-colors ${respectPrefs ? 'bg-blue-600' : 'bg-zinc-300'}`}
                data-testid="diet-prefs-toggle"
              >
                <span
                  className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${respectPrefs ? 'translate-x-5' : 'translate-x-0.5'}`}
                />
              </button>
            </div>
          )}


          {/* Vegetarian filter */}
          <div className="flex items-center justify-between bg-white border border-zinc-200 rounded-lg px-3 py-2">
            <span className="text-xs font-medium text-zinc-700 flex items-center gap-1.5">
              <Leaf className="w-3.5 h-3.5 text-green-600" />
              Vegetarian only
            </span>
            <button
              onClick={() => setVegOnly(!vegOnly)}
              role="switch"
              aria-checked={vegOnly}
              className={`relative w-10 h-5 rounded-full transition-colors ${vegOnly ? 'bg-green-600' : 'bg-zinc-300'}`}
              data-testid="diet-veg-toggle"
            >
              <span
                className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${vegOnly ? 'translate-x-5' : 'translate-x-0.5'}`}
              />
            </button>
          </div>

          {/* Meals grid */}
          {filteredMeals.length === 0 ? (
            <div className="bg-white border border-zinc-200 rounded-xl p-6 text-center">
              <p className="text-sm text-zinc-500">No meals in this combination yet: relax a filter or ask AI Chef below.</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3" data-testid="diet-meals-grid">
              {filteredMeals.map(meal => (
                <MealCard key={meal.id} meal={meal} onOpen={setSelectedMeal} lang={language} />
              ))}
            </div>
          )}

          {/* C1: Relax-a-filter hints (only when fewer than 8 results) */}
          {filteredMeals.length < 8 && relaxationHints.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3" data-testid="relax-hints">
              <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-amber-800 mb-2">
                <Sparkles className="w-3.5 h-3.5" />
                Want more options?
              </div>
              <div className="flex flex-wrap gap-1.5">
                {relaxationHints.map((h, i) => (
                  <button
                    key={i}
                    onClick={() => navigate('/diet-recovery')}
                    className="inline-flex items-center gap-1 text-[11px] bg-white border border-amber-300 hover:border-amber-500 hover:bg-amber-100 text-amber-900 px-2.5 py-1.5 rounded-full transition-colors"
                    data-testid={`relax-hint-${i}`}
                  >
                    <span className="font-semibold">+{h.unlocks}</span> if you relax <span className="font-semibold">{h.label}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* C2: AI Chef CTA (only when results are sparse) */}
          {filteredMeals.length < 8 && hasActivePrefs && (
            <div className="bg-gradient-to-br from-violet-500 to-fuchsia-600 text-white rounded-xl p-4" data-testid="ai-chef-cta">
              <div className="flex items-start gap-3">
                <div className="rounded-full bg-white/20 p-2 flex-shrink-0">
                  <Sparkles className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm font-bold uppercase tracking-wider mb-0.5">AI Chef</h3>
                  <p className="text-[12px] text-white/90 leading-snug mb-3">
                    Want 5 fresh recipes built around your exact preferences? AI Chef will generate them, with a recovery rationale on each.
                  </p>
                  <button
                    onClick={generateAiMeals}
                    disabled={aiLoading}
                    className="inline-flex items-center gap-1.5 bg-white text-violet-700 hover:bg-violet-50 font-bold uppercase tracking-wider text-[11px] px-3 py-2 rounded-lg disabled:opacity-60 transition-colors"
                    data-testid="ai-chef-generate-btn"
                  >
                    {aiLoading ? (<><RefreshCw className="w-3.5 h-3.5 animate-spin" /> Generating…</>) : (<><Sparkles className="w-3.5 h-3.5" /> Make 5 just for me</>)}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* C2: AI Chef results */}
          {aiMeals.length > 0 && (
            <div className="space-y-3" data-testid="ai-chef-results">
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-violet-600" />
                <h3 className="text-xs font-bold uppercase tracking-wider text-violet-700">AI Chef · {aiMeals.length} recipes</h3>
              </div>
              <div className="grid grid-cols-1 gap-2">
                {aiMeals.map((m) => (
                  <button
                    key={m.id}
                    onClick={() => setSelectedMeal(m)}
                    className="text-left bg-white border-2 border-violet-200 rounded-xl p-3 hover:border-violet-400 transition-colors relative"
                    data-testid={`ai-meal-${m.id}`}
                  >
                    <div className="absolute top-2 right-2 inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider bg-violet-100 text-violet-700 px-1.5 py-0.5 rounded-full">
                      <Sparkles className="w-2.5 h-2.5" />
                      AI Chef
                    </div>
                    <h4 className="text-sm font-bold text-zinc-900 pr-16">{m.title}</h4>
                    <div className="flex items-center gap-1.5 text-[10px] text-zinc-500 mt-1">
                      <span className="capitalize">{m.category}</span> · {m.country} · {m.prep_minutes + m.cook_minutes} min · {m.difficulty}
                    </div>
                    {m.recovery_note && (
                      <p className="text-[11px] text-zinc-600 mt-2 italic line-clamp-2">&ldquo;{m.recovery_note}&rdquo;</p>
                    )}
                    <div className="flex items-center justify-between mt-2">
                      <span
                        onClick={(e) => { e.stopPropagation(); toggleSaveRecipe(m); }}
                        role="button"
                        tabIndex={0}
                        className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-1 rounded-full cursor-pointer transition-colors ${
                          savedRecipeIds.has(m.id) ? 'bg-amber-100 text-amber-700' : 'bg-zinc-100 text-zinc-600 hover:bg-amber-50'
                        }`}
                        data-testid={`ai-meal-save-${m.id}`}
                      >
                        <Star className={`w-3 h-3 ${savedRecipeIds.has(m.id) ? 'fill-amber-500 text-amber-500' : ''}`} />
                        {savedRecipeIds.has(m.id) ? 'Saved' : 'Save'}
                      </span>
                      <span className="text-[10px] text-violet-600 font-semibold">View recipe →</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Disclaimer */}
          <div className="bg-zinc-50 border border-zinc-200 rounded-lg p-3 flex gap-2">
            <Info className="w-4 h-4 text-zinc-500 flex-shrink-0 mt-0.5" />
            <p className="text-[11px] text-zinc-600 leading-relaxed">{DIET_DISCLAIMER}</p>
          </div>
        </>
      )}

      <MealDetailModal meal={selectedMeal} onClose={() => setSelectedMeal(null)} prefs={prefs} />
    </div>
  );
}
